import { useMemo } from 'react';
import type { FinancialParams } from '../utils/financialDefaults';
import { calculateSensitivityHeatmap } from '../utils/financialCalculations';
import type { FinancialModelResult } from '../hooks/useFinancialModel';
import type { SimulationResults, SystemConfig } from '../utils/financialCalculations';
import { useState } from 'react';
import FinancialInputPanel from './FinancialInputPanel';
import FinancialKpiCards from './FinancialKpiCards';
import CashflowChart from './CashflowChart';
import InvestmentTable from './InvestmentTable';
import CostRevenueDonut from './CostRevenueDonut';
import SensitivityChart from './SensitivityChart';
import BreakEvenChart from './BreakEvenChart';
import MonteCarloChart from './MonteCarloChart';
import ScenarioComparison from './ScenarioComparison';
import DscrChart from './DscrChart';
import ProfitLossTable from './ProfitLossTable';
import CapexWaterfallChart from './CapexWaterfallChart';
import Co2ImpactCard from './Co2ImpactCard';
import SensitivityHeatmap from './SensitivityHeatmap';
import PaybackRiskChart from './PaybackRiskChart';
import InvestmentScorecard from './InvestmentScorecard';
import RevenueStackChart from './RevenueStackChart';
import GoalSeekPanel from './GoalSeekPanel';
import FleetProfileImport from './FleetProfileImport';
import EnergyPriceImport from './EnergyPriceImport';
import ConfigSummaryBar from './ConfigSummaryBar';

export default function FinancialDashboard({
  params,
  onParamChange,
  onChargerDetailChange,
  onCustomOpexChange,
  onCustomCapexChange,
  model,
  sys,
  sim,
  degradation,
  lockedParams,
  onToggleLock,
  bessLinked,
  onToggleBessLink,
  onFleetImport,
  fiscalEnabled,
  onToggleFiscal,
  scenarioA,
  profileDefaults,
  printMode,
  onResetToDefaults,
}: {
  params: FinancialParams;
  onParamChange: (key: keyof FinancialParams, value: number) => void;
  onChargerDetailChange?: (config: import('../utils/financialDefaults').ChargerConfig[] | undefined) => void;
  onCustomOpexChange?: (items: import('../utils/financialDefaults').CustomOpexItem[]) => void;
  onCustomCapexChange?: (items: import('../utils/financialDefaults').CustomCapexItem[]) => void;
  model: FinancialModelResult;
  sys: SystemConfig;
  sim: SimulationResults;
  degradation: number;
  lockedParams?: Set<keyof FinancialParams>;
  onToggleLock?: (key: keyof FinancialParams) => void;
  bessLinked?: boolean;
  onToggleBessLink?: () => void;
  onFleetImport?: (profile: number[]) => void;
  fiscalEnabled?: boolean;
  onToggleFiscal?: () => void;
  scenarioA?: FinancialModelResult | null;
  profileDefaults?: { pv: number; bE: number; bP: number; eff: number; dod: number; buP: number; gMax: number; dem: number; deg: number };
  printMode?: boolean;
  onResetToDefaults?: () => void;
}) {
  const [heatmapXKey, setHeatmapXKey] = useState<keyof FinancialParams>('chargingTariff');
  const [heatmapYKey, setHeatmapYKey] = useState<keyof FinancialParams>('bessCapex');

  const heatmap = useMemo(
    () => calculateSensitivityHeatmap(sim, sys, params, degradation, heatmapXKey, heatmapYKey),
    [sim, sys, params, degradation, heatmapXKey, heatmapYKey]
  );

  return (
    <div>
      <div className="hidden print:block mb-4 p-4 bg-[#F5F8FB] rounded-xl border border-[#D9E2EC]">
        <div className="text-xs font-bold text-[#0B1F4A] mb-2">📄 Business Case Samenvatting</div>
        <div className="text-[10px] text-[#8899AA] mb-2">
          Geprint op {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <div><span className="text-[#8899AA]">PV:</span> <strong>{sys.pvCapacity} MWp</strong></div>
          <div><span className="text-[#8899AA]">BESS:</span> <strong>{sys.bessEnergy} MWh</strong></div>
          <div><span className="text-[#8899AA]">Backup:</span> <strong>{sys.backupPower} MW</strong></div>
          <div><span className="text-[#8899AA]">Grid:</span> <strong>{sys.gridMax} kW</strong></div>
          <div><span className="text-[#8899AA]">Trucks:</span> <strong>{sys.numTrucks}</strong></div>
          <div><span className="text-[#8899AA]">Laadtarief:</span> <strong>€{params.chargingTariff.toFixed(2)}/kWh</strong></div>
          <div><span className="text-[#8899AA]">WACC:</span> <strong>{params.wacc}%</strong></div>
          <div><span className="text-[#8899AA]">Duur:</span> <strong>{params.projectDuration} jaar</strong></div>
          <div><span className="text-[#8899AA]">Degradatie:</span> <strong>{degradation}%/j</strong></div>
        </div>
      </div>

      <div className="mb-1">
        <div className="text-sm font-bold text-[#0B1F4A]">💰 Business Case — Financieel Model</div>
        <div className="text-[11px] text-[#8899AA] mb-4">
          {params.projectDuration}-jarig financieel model op basis van de simulatieresultaten
        </div>
      </div>

      {!printMode && (
        <div className="print:hidden">
          <FinancialInputPanel
            params={params}
            onChange={onParamChange}
            onChargerDetailChange={onChargerDetailChange}
            onCustomOpexChange={onCustomOpexChange}
            onCustomCapexChange={onCustomCapexChange}
            lockedParams={lockedParams}
            onToggleLock={onToggleLock}
            bessLinked={bessLinked}
            onToggleBessLink={onToggleBessLink}
            fiscalEnabled={fiscalEnabled}
            onToggleFiscal={onToggleFiscal}
          />
        </div>
      )}

      {!printMode && (
        <div className="print:hidden">
          <GoalSeekPanel params={params} sim={sim} sys={sys} degradation={degradation} onApply={onParamChange} />
        </div>
      )}

      {scenarioA && <ScenarioComparison scenarioA={scenarioA} scenarioB={model} />}

      {profileDefaults && (
        <div data-pdf-section="bc-config-summary">
          <ConfigSummaryBar
            sys={sys}
            sim={sim}
            params={params}
            degradation={degradation}
            kpis={model.kpis}
            capex={model.capex}
            cashflows={model.cashflows}
            profileDefaults={profileDefaults}
            onResetToDefaults={onResetToDefaults}
          />
        </div>
      )}

      <div data-pdf-section="bc-kpis"><FinancialKpiCards kpis={model.kpis} /></div>
      <div data-pdf-section="bc-co2"><Co2ImpactCard kpis={model.kpis} cashflows={model.cashflows} /></div>
      <div data-pdf-section="bc-capex"><CapexWaterfallChart capex={model.capex} printMode={printMode} /></div>
      <div data-pdf-section="bc-cashflow"><CashflowChart cashflows={model.cashflows} printMode={printMode} /></div>
      <div data-pdf-section="bc-revenue-stack"><RevenueStackChart cashflows={model.cashflows} printMode={printMode} /></div>
      <div data-pdf-section="bc-dscr"><DscrChart cashflows={model.cashflows} loanTerm={params.loanTerm} printMode={printMode} /></div>
      <div data-pdf-section="bc-pnl"><ProfitLossTable cashflows={model.cashflows} params={params} sim={sim} sys={sys} degradation={degradation} printMode={printMode} /></div>
      <div data-pdf-section="bc-investment"><InvestmentTable capex={model.capex} sys={sys} fp={params} /></div>
      <div data-pdf-section="bc-donut"><CostRevenueDonut costStructure={model.costStructure} revenueStructure={model.revenueStructure} printMode={printMode} /></div>
      <div data-pdf-section="bc-sensitivity"><SensitivityChart sensitivity={model.sensitivity} printMode={printMode} /></div>
      <div data-pdf-section="bc-heatmap">
        <SensitivityHeatmap
          cells={heatmap.cells} xValues={heatmap.xValues} yValues={heatmap.yValues}
          xLabel={heatmap.xLabel} yLabel={heatmap.yLabel}
          onAxisChange={printMode ? undefined : (xKey, yKey) => { setHeatmapXKey(xKey); setHeatmapYKey(yKey); }}
        />
      </div>
      <div data-pdf-section="bc-breakeven"><BreakEvenChart sim={sim} sys={sys} fp={params} degradation={degradation} printMode={printMode} /></div>
      <div data-pdf-section="bc-payback-risk"><PaybackRiskChart monteCarlo={model.monteCarlo} printMode={printMode} /></div>
      <div data-pdf-section="bc-montecarlo"><MonteCarloChart monteCarlo={model.monteCarlo} printMode={printMode} /></div>
      <div data-pdf-section="bc-scorecard"><InvestmentScorecard kpis={model.kpis} wacc={params.wacc} printMode={printMode} /></div>

      {!printMode && (
        <div className="print:hidden mt-6">
          <div className="text-sm font-bold text-[#0B1F4A] mb-3">📂 Data & Import</div>
          {onFleetImport && <FleetProfileImport onImport={onFleetImport} />}
          <EnergyPriceImport />
        </div>
      )}
    </div>
  );
}
